import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Button,
  Divider,
  Tooltip,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { v4 as uuid } from "uuid";
import {
  updatePredefinedComments,
  deletePredefinedComments,
} from "../../api/recognitionApi";
import { submitApproval } from "../../api/approvalApi";
import { Locale } from "../../utils/Locales";
import { useLocalization } from "../../context/LocalizationContext";
import { useToast } from "../../context/ToastContext";
import { CONTENT_TYPES } from "../../constants/contentTypes";
import { usePermissions } from "../../hooks/usePermissions";
import ApprovalStatusBadge from "../approval/ApprovalStatusBadge";
import SubmitForApprovalButton from "../approval/SubmitForApprovalButton";
import WithdrawButton from "../approval/WithdrawButton";
import ContentPreviewDrawer from "../approval/ContentPreviewDrawer";
import ConfirmModal from "../form-components/ConfirmModal";
import CommentModal from "./CommentModal";
import LocalText from "../common/LocalText";

const viewBtnSx = {
  borderColor: "#3b82f6",
  color: "#1d4ed8",
  bgcolor: "#eff6ff",
  borderRadius: "8px",
  px: 1.5,
  py: 0.5,
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  whiteSpace: "nowrap",
  boxShadow: "none",
  "&:hover": {
    bgcolor: "#dbeafe",
    borderColor: "#2563eb",
    boxShadow: "none",
  },
};

const CommentCard = ({ comment, onSuccess }) => {
  const { locale, availableLocales } = useLocalization();
  const { toast } = useToast();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/recognition', 'recognition-edit');

  // ── modal state ─────────────────────────────────────────────────────────────
  const [deleteModalOpen, setDeleteModalOpen]   = useState(false);
  const [proposeDeleteOpen, setProposeDeleteOpen] = useState(false);
  const [editOpen, setEditOpen]                 = useState(false);
  const [editInitialData, setEditInitialData]   = useState(null);
  const [editReadOnly, setEditReadOnly]         = useState(false);
  const [editApprovalMode, setEditApprovalMode] = useState(false);
  const [viewProposalOpen, setViewProposalOpen] = useState(false);

  // ── derived state ────────────────────────────────────────────────────────────
  // Flow A: draft + no ca record → direct edit/delete + submit for approval
  // Flow B: approved + no pending/rejected ca → propose edit/delete
  // Flow C: pending or rejected → locked (eye icon only on live card)
  const isSnapshot    = comment._viewType === "snapshot";
  const isLocked      = comment.approvalStatus === "pending" || comment.approvalStatus === "rejected";
  const canDirectEdit = comment.contentStatus === "draft" && !isLocked;

  const liveId      = comment._liveId ?? comment.id;
  const commentName = comment.localizedPairs?.values?.[0]?.value;
  const localeText  = comment.localizedPairs?.values?.find((v) => v.localeId === locale)?.value || null;
  const langCount   = comment.localizedPairs?.values?.length || 0;
  const localeName  = Locale[locale] || locale;

  const viewApprovalRecord = comment.approvalId
    ? {
        approvalId:   comment.approvalId,
        actionType:   comment.approvalActionType,
        contentType:  CONTENT_TYPES.RECOGNITION,
        sectionLabel: "Recognition",
        contentName:  commentName,
        status:       comment.approvalStatus,
      }
    : null;

  // ── edit handlers ────────────────────────────────────────────────────────────

  const openViewModal = () => {
    setEditInitialData(comment);
    setEditReadOnly(true);
    setEditApprovalMode(false);
    setEditOpen(true);
  };

  const openDirectEdit = () => {
    setEditInitialData(comment);
    setEditReadOnly(false);
    setEditApprovalMode(false);
    setEditOpen(true);
  };

  const openProposeEdit = () => {
    setEditInitialData(comment);
    setEditReadOnly(false);
    setEditApprovalMode(true);
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditInitialData(null);
    setEditReadOnly(false);
    setEditApprovalMode(false);
  };

  const handleEditSave = async (localizedPairs, commentId) => {
    try {
      if (editApprovalMode) {
        const snapshot = {
          localizedPairs: {
            key: comment.localizedPairs?.key || uuid(),
            values: localizedPairs.values,
          },
          isActive: true,
        };
        await submitApproval({
          contentType:      CONTENT_TYPES.RECOGNITION,
          contentId:        commentId ?? liveId,
          contentName:      localizedPairs.values?.[0]?.value || commentName,
          actionType:       "edit",
          proposedData:     JSON.stringify(snapshot),
          proposedSnapshot: JSON.stringify(snapshot),
        });
        toast.success("Edit submitted for approval!");
      } else {
        await updatePredefinedComments(commentId ?? liveId, {
          id:             commentId ?? liveId,
          isActive:       true,
          localizedPairs: {
            key:    comment.localizedPairs?.key || uuid(),
            values: localizedPairs.values,
          },
        });
        toast.success("Comment updated");
      }
      onSuccess?.(editApprovalMode ? 'pending' : 'draft');
    } catch {
      // error toast handled by api interceptor
    }
  };

  // ── delete handlers ──────────────────────────────────────────────────────────

  const handleDirectDelete = async () => {
    try {
      await deletePredefinedComments(liveId);
      toast.success("Comment deleted");
      setDeleteModalOpen(false);
      onSuccess?.();
    } catch {
      // error toast from interceptor
    }
  };

  const handleProposeDelete = async () => {
    try {
      await submitApproval({
        contentType:  CONTENT_TYPES.RECOGNITION,
        contentId:    liveId,
        contentName:  commentName,
        actionType:   "delete",
      });
      toast.success("Deletion request submitted for approval.");
      setProposeDeleteOpen(false);
      onSuccess?.('pending');
    } catch {
      // error toast from interceptor
    }
  };

  // ── tooltips ─────────────────────────────────────────────────────────────────

  const editTooltip = isLocked
    ? comment.approvalStatus === "pending"
      ? "A pending proposal exists — view or withdraw it first"
      : "A rejected proposal exists — view or dismiss it first"
    : canDirectEdit
      ? "Edit comment"
      : "Propose edit (requires approval)";

  const deleteTooltip = isLocked
    ? comment.approvalStatus === "pending"
      ? "A pending proposal exists — withdraw it first"
      : "A rejected proposal exists — dismiss it first"
    : canDirectEdit
      ? "Delete comment"
      : "Propose deletion (requires approval)";

  // ── render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <Card
        sx={{
          bgcolor: "white",
          borderRadius: 4,
          border: "2px solid transparent",
          boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          transition: "all 0.22s ease",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(10,50,109,0.15)",
            borderColor: "#5383b3",
            transform: "translateY(-3px)",
          },
        }}
      >
        {/* Snapshot banner */}
        {isSnapshot && (
          <Box
            sx={{
              bgcolor:
                comment.approvalStatus === "pending" ? "#d97706" : "#dc2626",
              color: "white",
              px: 1.5,
              py: 0.5,
              fontSize: 11,
              fontWeight: 600,
              textAlign: "center",
              borderRadius: "16px 16px 0 0",
            }}
          >
            {comment.approvalStatus === "pending"
              ? "Proposed — Pending Review"
              : "Proposed — Rejected"}
          </Box>
        )}

        <CardContent sx={{ flexGrow: 1, p: 3 }}>
          {/* Top row: language pill + active dot */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={0.75}
              sx={{
                px: 1.5,
                py: 0.5,
                bgcolor: "#f1f5f9",
                borderRadius: 2,
                border: "1px solid #e2e8f0",
              }}
            >
              <LanguageIcon sx={{ fontSize: 14, color: "#85a5c6" }} />
              <Typography
                variant="caption"
                sx={{
                  color: "#85a5c6",
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                  fontSize: 10,
                }}
              >
                {langCount}{" "}
                <LocalText>{langCount === 1 ? "Language" : "Languages"}</LocalText>
              </Typography>
            </Box>

            {/* Active dot — hidden for now
            {!isSnapshot && (
              <Box display="flex" alignItems="center" gap={0.75}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: comment.isActive ? "#4caf50" : "#bdbdbd",
                    "@keyframes pulse": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0.45 },
                    },
                    animation: comment.isActive
                      ? "pulse 2s ease-in-out infinite"
                      : "none",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: comment.isActive ? "#2e7d32" : "#9e9e9e",
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    fontSize: 10,
                  }}
                >
                  <LocalText>{comment.isActive ? "ACTIVE" : "INACTIVE"}</LocalText>
                </Typography>
              </Box>
            )}
            */}
          </Box>

          {/* Comment text — current locale */}
          <Box sx={{ pl: 2, borderLeft: "2px solid #e2e8f0" }}>
            {localeText ? (
              <>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#1e3a5f",
                    fontWeight: 500,
                    lineHeight: 1.55,
                    fontSize: 16,
                  }}
                >
                  {localeText}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.75,
                    color: "#85a5c6",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    fontSize: 10,
                  }}
                >
                  {localeName}
                </Typography>
              </>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "#b0bec5", fontStyle: "italic" }}
              >
                <LocalText>No translation for this language</LocalText>
              </Typography>
            )}
          </Box>
        </CardContent>

        {/* Approval row */}
        {(comment.contentStatus || comment.approvalStatus) && (
          <Box
            sx={{
              px: 3,
              pb: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {/* Badge: live card shows contentStatus; snapshot shows approvalStatus */}
            <ApprovalStatusBadge
              status={!isSnapshot ? comment.contentStatus : comment.approvalStatus}
              actionType={comment.approvalActionType}
            />

            {/* Flow A: new draft with no ca record → first submit */}
            {!isSnapshot && canDirectEdit && canEdit && (
              <SubmitForApprovalButton
                contentType={CONTENT_TYPES.RECOGNITION}
                contentId={comment.id}
                contentName={commentName}
                onSubmitted={() => onSuccess?.('pending')}
              />
            )}

            {/* Snapshot → View proposal */}
            {isSnapshot && viewApprovalRecord && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
                onClick={() => setViewProposalOpen(true)}
                sx={viewBtnSx}
              >
                View
              </Button>
            )}

            {/* Snapshot pending → Withdraw */}
            {isSnapshot &&
              comment.approvalStatus === "pending" &&
              comment.approvalId &&
              canEdit && (
                <WithdrawButton
                  approvalId={comment.approvalId}
                  onWithdrawn={() => onSuccess?.(comment.approvalActionType === 'create' ? 'draft' : 'live')}
                />
              )}

            {/* Snapshot rejected → Dismiss */}
            {isSnapshot &&
              comment.approvalStatus === "rejected" &&
              comment.approvalId &&
              canEdit && (
                <WithdrawButton
                  approvalId={comment.approvalId}
                  variant="dismiss"
                  onWithdrawn={() => onSuccess?.(comment.approvalActionType === 'create' ? 'draft' : 'live')}
                />
              )}
          </Box>
        )}

        {/* Action row — live cards only */}
        {!isSnapshot && (isLocked || canEdit) && (
          <>
            <Divider sx={{ borderColor: "rgba(0,0,0,0.07)" }} />
            <CardActions sx={{ px: 3, py: 1.5, justifyContent: "flex-end" }}>
              {/* Active toggle — hidden for now
              {canEdit && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Switch
                    checked={comment.isActive}
                    size="small"
                    disabled={isLocked}
                    onChange={(e) => {
                      setPendingToggle({ id: comment.id, isActive: e.target.checked });
                      setToggleModalOpen(true);
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#9e9e9e",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontSize: 11,
                    }}
                  >
                    <LocalText>Active</LocalText>
                  </Typography>
                </Box>
              )}
              */}

              {/* Edit / eye + delete */}
              <Box display="flex" alignItems="center" gap={0.5}>
                {isLocked ? (
                  <Tooltip title="View current content (read-only)">
                    <IconButton
                      size="small"
                      onClick={openViewModal}
                      sx={{
                        color: "#3b82f6",
                        borderRadius: 2,
                        p: 1,
                        "&:hover": { color: "#1d4ed8", bgcolor: "#eff6ff" },
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  canEdit && (
                    <Tooltip title={editTooltip}>
                      <IconButton
                        size="small"
                        onClick={canDirectEdit ? openDirectEdit : openProposeEdit}
                        sx={{
                          color: "#85a5c6",
                          borderRadius: 2,
                          p: 1,
                          "&:hover": { color: "#0a326d", bgcolor: "#EAF0F6" },
                          transition: "all 0.18s ease",
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )
                )}

                {canEdit && (
                  <Tooltip title={deleteTooltip}>
                    {/* span wrapper lets Tooltip work on a disabled button */}
                    <span>
                      <IconButton
                        size="small"
                        disabled={isLocked}
                        onClick={
                          canDirectEdit
                            ? () => setDeleteModalOpen(true)
                            : () => setProposeDeleteOpen(true)
                        }
                        sx={{
                          color: "#85a5c6",
                          borderRadius: 2,
                          p: 1,
                          "&:hover": { color: "#dc2626", bgcolor: "#fff1f2" },
                          transition: "all 0.18s ease",
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </CardActions>
          </>
        )}
      </Card>

      {/* Edit / View modal */}
      <CommentModal
        open={editOpen}
        onClose={handleCloseEdit}
        onSave={handleEditSave}
        locales={availableLocales}
        initialData={editInitialData}
        readOnly={editReadOnly}
        approvalMode={editApprovalMode}
      />

      {/* Active toggle confirm — hidden for now
      <ConfirmModal
        open={toggleModalOpen}
        onCancel={() => {
          setToggleModalOpen(false);
          setPendingToggle(null);
        }}
        onConfirm={handleToggleStatus}
        severity="warning"
        title="Change Comment Status"
        message="Are you sure you want to change this comment's status?"
        confirmLabel="Confirm"
      />
      */}

      {/* Direct delete confirm */}
      <ConfirmModal
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={handleDirectDelete}
        severity="danger"
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmLabel="Delete"
      />

      {/* Propose deletion confirm */}
      <ConfirmModal
        open={proposeDeleteOpen}
        onCancel={() => setProposeDeleteOpen(false)}
        onConfirm={handleProposeDelete}
        severity="warning"
        title="Propose Deletion"
        message="Submit a deletion request for this comment? It stays active until an approver approves the deletion."
        confirmLabel="Submit for Approval"
      />

      {/* View proposal (snapshot card View button) */}
      {viewApprovalRecord && (
        <ContentPreviewDrawer
          open={viewProposalOpen}
          approvalRecord={viewApprovalRecord}
          onClose={() => setViewProposalOpen(false)}
          readOnly
          onEdit={canEdit ? (proposedSnap) => {
            setViewProposalOpen(false);
            setEditInitialData({ ...(proposedSnap ?? {}), id: liveId });
            setEditReadOnly(false);
            setEditApprovalMode(true);
            setEditOpen(true);
          } : undefined}
        />
      )}
    </>
  );
};

export default CommentCard;
